import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/SalesTaxReport.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';

function formatCurrency(v) {
  return Number(v || 0).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

export default function SalesTaxReportPage() {
  const userId = localStorage.getItem('userId');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({
    billedTax: 0,
    collectedTax: 0,
    byLocation: [],
    invoiceCount: 0,
  });

  const [projectId, setProjectId] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(todayYmd());

  useEffect(() => {
    async function loadProjects() {
      if (!userId) return setProjects([]);
      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }
    loadProjects();
  }, [userId]);

  useEffect(() => {
    async function loadReport() {
      if (!userId) {
        setReport({ billedTax: 0, collectedTax: 0, byLocation: [], invoiceCount: 0 });
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (projectId) params.set('projectId', projectId);

        const res = await fetch(`${API_BASE}/api/reports/sales-tax?${params.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setReport({
          billedTax: Number(data?.billedTax || 0),
          collectedTax: Number(data?.collectedTax || 0),
          byLocation: Array.isArray(data?.byLocation) ? data.byLocation : [],
          invoiceCount: Number(data?.invoiceCount || 0),
        });
      } catch (err) {
        console.error('Failed to load sales tax report', err);
        setReport({ billedTax: 0, collectedTax: 0, byLocation: [], invoiceCount: 0 });
      } finally {
        setLoading(false);
      }
    }

    loadReport();
  }, [userId, startDate, endDate, projectId]);

  const selectedProjectName = useMemo(() => {
    if (!projectId) return 'All Projects';
    return projects.find((p) => p.id === projectId)?.name || 'Selected Project';
  }, [projectId, projects]);

  function exportExcel() {
    const headers = ['Tax Location', 'Invoices', 'Billed Tax', 'Collected Tax'];
    const rows = (report.byLocation || []).map((r) => [
      r.locationName || 'Unassigned',
      Number(r.invoiceCount || 0),
      Number(r.billedTax || 0),
      Number(r.collectedTax || 0),
    ]);

    exportRowsToExcel({
      filename: 'sales-tax-report.xlsx',
      sheetName: 'Sales Tax',
      headers,
      rows,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Sales Tax Report</h1>
            <div className="muted">
              Billed vs. collected tax by location for {selectedProjectName}
            </div>
          </div>
          <div className="report-actions">
            <button className="report-btn-secondary" onClick={exportExcel}>
              <Download size={16} />
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <input
            className="report-select"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            className="report-select"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(report.billedTax)}</div>
              <div className="report-metric-label">Billed Tax</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(report.collectedTax)}</div>
              <div className="report-metric-label">Collected Tax</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{Number(report.invoiceCount || 0)}</div>
              <div className="report-metric-label">Invoices</div>
            </div>
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading sales tax report...</div>
          ) : !report.byLocation?.length ? (
            <div className="report-empty">No sales tax entries found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Sales tax report by location">
              <thead>
                <tr>
                  <th>Tax Location</th>
                  <th className="right">Invoices</th>
                  <th className="right">Billed Tax</th>
                  <th className="right">Collected Tax</th>
                </tr>
              </thead>
              <tbody>
                {report.byLocation.map((r) => (
                  <tr key={`${r.locationId || 'unassigned'}-${r.locationName || 'Unassigned'}`}>
                    <td>{r.locationName || 'Unassigned'}</td>
                    <td className="right">{Number(r.invoiceCount || 0)}</td>
                    <td className="right">{formatCurrency(r.billedTax)}</td>
                    <td className="right">{formatCurrency(r.collectedTax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
